(function() {
  'use strict';

  /**
   * Expected Delivery Counter
   * Calculates and displays countdown timers for delivery notices
   */
  
  class ExpectedDeliveryCounter {
    constructor(container) {
      this.container = container;
      this.thresholdTime = this.parseTime(container.getAttribute('data-counter-time'));
      this.beforeTimeSpan = container.querySelector('[data-counter-before-time-text]');
      this.afterTimeSpan = container.querySelector('[data-counter-after-time-text]');
      this.beforeTimeEm = this.beforeTimeSpan?.querySelector('em');
      this.afterTimeEm = this.afterTimeSpan?.querySelector('em');
      
      if (!this.thresholdTime || (!this.beforeTimeEm && !this.afterTimeEm)) {
        return; // Invalid configuration, exit
      }
      
      this.updateInterval = null;
      this.init();
    }

    /**
     * Parse time string (e.g., "14:00" or "14:00:00") into hours and minutes
     * @param {string} timeString - Time in HH:MM or HH:MM:SS format
     * @returns {Object|null} Object with hours and minutes, or null if invalid
     */
    parseTime(timeString) {
      if (!timeString || typeof timeString !== 'string') {
        return null;
      }

      const timeMatch = timeString.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (!timeMatch) {
        return null;
      }

      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);

      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
      }

      return { hours, minutes };
    }

    /**
     * Get current date/time in local timezone
     * @returns {Date} Current date object
     */
    getCurrentTime() {
      return new Date();
    }

    /**
     * Create a Date object for today at the threshold time
     * @returns {Date} Date object for today at threshold time
     */
    getThresholdToday() {
      const now = this.getCurrentTime();
      const threshold = new Date();
      threshold.setHours(this.thresholdTime.hours, this.thresholdTime.minutes, 0, 0);
      return threshold;
    }

    /**
     * Create a Date object for tomorrow at the threshold time
     * @returns {Date} Date object for tomorrow at threshold time
     */
    getThresholdTomorrow() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(this.thresholdTime.hours, this.thresholdTime.minutes, 0, 0);
      return tomorrow;
    }

    /**
     * Calculate time difference and format as countdown string
     * @param {Date} targetDate - Target date to countdown to
     * @returns {string} Formatted countdown string (e.g., "4:46:15")
     */
    formatCountdown(targetDate) {
      const now = this.getCurrentTime();
      const diff = targetDate - now;

      if (diff <= 0) {
        return '0:00:00';
      }

      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    /**
     * Format time as HH:MM
     * @param {Date} date - Date object
     * @returns {string} Formatted time string
     */
    formatTime(date) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    /**
     * Check if current time is before the threshold
     * @returns {boolean} True if before threshold, false if after
     */
    isBeforeThreshold() {
      const now = this.getCurrentTime();
      const threshold = this.getThresholdToday();
      return now < threshold;
    }

    /**
     * Update the display with current countdown values
     */
    update() {
      const isBefore = this.isBeforeThreshold();

      if (isBefore) {
        // Show before-time span, hide after-time span
        if (this.beforeTimeSpan) {
          this.beforeTimeSpan.style.display = '';
        }
        if (this.afterTimeSpan) {
          this.afterTimeSpan.style.display = 'none';
        }

        // Update countdown to threshold
        if (this.beforeTimeEm) {
          const threshold = this.getThresholdToday();
          this.beforeTimeEm.textContent = this.formatCountdown(threshold);
        }
      } else {
        // Show after-time span, hide before-time span
        if (this.beforeTimeSpan) {
          this.beforeTimeSpan.style.display = 'none';
        }
        if (this.afterTimeSpan) {
          this.afterTimeSpan.style.display = '';
        }

        // Update time to order for tomorrow
        if (this.afterTimeEm) {
          const tomorrowThreshold = this.getThresholdTomorrow();
          this.afterTimeEm.textContent = this.formatTime(tomorrowThreshold);
        }
      }
    }

    /**
     * Initialize the counter and start updating
     */
    init() {
      // Initial update
      this.update();

      // Update every second for real-time countdown
      this.updateInterval = setInterval(() => {
        this.update();
      }, 1000); // 1 second

      // Also update when crossing the threshold
      const now = this.getCurrentTime();
      const threshold = this.getThresholdToday();
      const msUntilThreshold = threshold - now;

      if (msUntilThreshold > 0) {
        // Schedule update at threshold time
        setTimeout(() => {
          this.update();
          // After threshold, update every second
          if (this.updateInterval) {
            clearInterval(this.updateInterval);
          }
          this.updateInterval = setInterval(() => {
            this.update();
          }, 1000);
        }, msUntilThreshold);
      }
    }

    /**
     * Clean up intervals
     */
    destroy() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    }
  }

  /**
   * Initialize all delivery counters on the page
   */
  function initDeliveryCounters() {
    const containers = document.querySelectorAll('[data-counter-time]');
    const counters = [];

    containers.forEach(container => {
      const counter = new ExpectedDeliveryCounter(container);
      if (counter.thresholdTime) {
        counters.push(counter);
      }
    });

    // Store counters for potential cleanup
    if (counters.length > 0) {
      window.expectedDeliveryCounters = counters;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeliveryCounters);
  } else {
    initDeliveryCounters();
  }

  // Re-initialize on dynamic content updates (e.g., AJAX product loads)
  if (typeof document.addEventListener !== 'undefined') {
    // Listen for custom events that might indicate content updates
    document.addEventListener('shopify:section:load', initDeliveryCounters);
  }
})();